/**
 * ImpersonateBanner.jsx
 * Bannière affichée quand un admin est en mode impersonation
 * Permet de quitter le mode et revenir au compte admin
 */

import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, ArrowLeft, Shield } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { getToken, setToken as setStoredToken, clearToken } from '../services/tokenService';

const API = process.env.REACT_APP_BACKEND_URL;

export const ImpersonateBanner = () => {
  const [impersonateStatus, setImpersonateStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const checkImpersonateStatus = useCallback(async () => {
    const token = getToken();
    if (!token) return;

    try {
      const response = await axios.get(`${API}/api/admin/impersonate/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.is_impersonating) {
        setImpersonateStatus(response.data);
      } else {
        setImpersonateStatus(null);
      }
    } catch {
      // Silently fail - user is not impersonating
      setImpersonateStatus(null);
    }
  }, []);

  useEffect(() => {
    checkImpersonateStatus();
    
    // Vérifier périodiquement (au cas où le token expire)
    const interval = setInterval(checkImpersonateStatus, 60000);
    return () => clearInterval(interval);
  }, [checkImpersonateStatus]);

  // Écouter les changements de localStorage (login/logout)
  useEffect(() => {
    const handleStorageChange = () => {
      checkImpersonateStatus();
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('impersonate-start', checkImpersonateStatus);
    window.addEventListener('impersonate-end', checkImpersonateStatus);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('impersonate-start', checkImpersonateStatus);
      window.removeEventListener('impersonate-end', checkImpersonateStatus);
    };
  }, [checkImpersonateStatus]);

  const handleExitImpersonate = async () => {
    setLoading(true);
    try {
      const token = getToken();
      
      await axios.post(`${API}/api/admin/impersonate/end`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Récupérer le token admin original
      const originalAdminToken = localStorage.getItem('original_admin_token');
      
      if (originalAdminToken) {
        // Restaurer le token admin
        setStoredToken(originalAdminToken);
        localStorage.removeItem('original_admin_token');
        localStorage.removeItem('impersonated_user');
        
        toast.success('Session d\'impersonation terminée. Vous êtes reconnecté en tant qu\'administrateur.');
        
        // Déclencher un événement pour mettre à jour l'UI
        window.dispatchEvent(new Event('impersonate-end'));
        
        // Rafraîchir la page pour recharger les données admin
        setTimeout(() => {
          window.location.href = '/admin/members';
        }, 500);
      } else {
        // Pas de token original, déconnecter
        clearToken();
        toast.info('Session terminée. Veuillez vous reconnecter.');
        window.location.href = '/login';
      }
      
      setImpersonateStatus(null);
    } catch {
      toast.error('Erreur lors de la fin de la session d\'impersonation');
    } finally {
      setLoading(false);
    }
  };

  if (!impersonateStatus) return null;

  const { impersonated_user, original_admin_email } = impersonateStatus;

  return (
    <div className="fixed top-0 left-0 right-0 z-[9999] bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 text-white shadow-lg">
      <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 bg-white/20 px-3 py-1 rounded-full">
            <Shield className="h-4 w-4" />
            <span className="text-xs font-bold uppercase tracking-wide">Mode Impersonate</span>
          </div>
          
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 animate-pulse" />
            <span className="font-medium">
              Vous agissez en tant que: <strong>{impersonated_user?.prenom} {impersonated_user?.nom}</strong>
              <span className="opacity-80 ml-1">({impersonated_user?.email})</span>
            </span>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <span className="text-xs opacity-80">
            Admin: {original_admin_email}
          </span>
          
          <Button
            onClick={handleExitImpersonate}
            disabled={loading}
            size="sm"
            className="bg-white text-red-600 hover:bg-red-50 font-semibold"
          >
            {loading ? (
              <>
                <span className="animate-spin mr-2">⏳</span>
                Sortie...
              </>
            ) : (
              <>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Quitter le mode Impersonate
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ImpersonateBanner;
